<!-- NOTICE: KAPTION-INTEGRATION -->
<!-- Schedule Message Popover - Allows scheduling messages for future delivery -->
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import {
  addMinutes,
  addHours,
  format,
  isBefore,
  startOfMinute,
} from 'date-fns';
import {
  es as esLocale,
  pt as ptLocale,
  fr as frLocale,
  de as deLocale,
  it as itLocale,
  tr as trLocale,
  enUS as enLocale,
} from 'date-fns/locale';
import * as chrono from 'chrono-node';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionSendIcon from 'dashboard/components/icons/KaptionSendIcon.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'schedule']);

const { locale, t } = useI18n();

// State
const inputValue = ref('');
const parsedDate = ref(null);
const selectedTimezone = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);
// Quick choice the agent picked (its key), or '' when typing another time.
const selectedKey = ref('');
// Settings (timezone) live in their own collapsed section of the popover.
const showSettings = ref(false);

// Common timezones for quick access
const COMMON_TIMEZONES = [
  {
    value: 'America/New_York',
    label: 'New York (EST/EDT)',
    region: 'Americas',
  },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)', region: 'Americas' },
  {
    value: 'America/Los_Angeles',
    label: 'Los Angeles (PST/PDT)',
    region: 'Americas',
  },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', region: 'Americas' },
  {
    value: 'America/Buenos_Aires',
    label: 'Buenos Aires (ART)',
    region: 'Americas',
  },
  {
    value: 'America/Mexico_City',
    label: 'Mexico City (CST/CDT)',
    region: 'Americas',
  },
  { value: 'America/Bogota', label: 'Bogota (COT)', region: 'Americas' },
  { value: 'America/Lima', label: 'Lima (PET)', region: 'Americas' },
  { value: 'America/Santiago', label: 'Santiago (CLT)', region: 'Americas' },
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', region: 'Europe' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai (IST)', region: 'Asia' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', region: 'Pacific' },
  {
    value: 'Pacific/Auckland',
    label: 'Auckland (NZST/NZDT)',
    region: 'Pacific',
  },
  { value: 'UTC', label: 'UTC', region: 'UTC' },
];

// Get UTC offset for a timezone (simplified format like "GMT-3")
const getTimezoneOffset = timezone => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    return offsetPart ? offsetPart.value : 'UTC';
  } catch {
    return 'UTC';
  }
};

// Simplified timezone display (just GMT offset)
const simplifiedTimezone = computed(() => {
  return getTimezoneOffset(selectedTimezone.value);
});

// The detected zone may be missing from the common list; always offer it.
const timezoneOptions = computed(() => {
  const known = COMMON_TIMEZONES.some(
    tz => tz.value === selectedTimezone.value
  );
  if (known || !selectedTimezone.value) return COMMON_TIMEZONES;
  return [
    {
      value: selectedTimezone.value,
      label: selectedTimezone.value.replace(/_/g, ' '),
    },
    ...COMMON_TIMEZONES,
  ];
});
const timezoneOptionLabel = tz =>
  `${tz.label} · ${getTimezoneOffset(tz.value)}`;
const timezoneLabel = computed(() => {
  const match = timezoneOptions.value.find(
    tz => tz.value === selectedTimezone.value
  );
  const name = (match?.label || selectedTimezone.value).replace(
    / \([^)]*\)$/,
    ''
  );
  return `${name} · ${simplifiedTimezone.value}`;
});

// Detect user's timezone on mount
onMounted(() => {
  try {
    selectedTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    selectedTimezone.value = 'UTC';
  }
});

// Get date-fns locale based on current i18n locale
const getDateFnsLocale = () => {
  const lang = locale.value.split('-')[0];
  const locales = {
    en: enLocale,
    es: esLocale,
    pt: ptLocale,
    fr: frLocale,
    de: deLocale,
    it: itLocale,
    tr: trLocale,
  };
  return locales[lang] || enLocale;
};

// Get the appropriate chrono parser for the current locale
const getChronoParser = () => {
  const lang = locale.value.split('-')[0];
  const parsers = {
    en: chrono.en,
    es: chrono.es,
    pt: chrono.pt,
    fr: chrono.fr,
    de: chrono.de,
    nl: chrono.nl,
    ja: chrono.ja,
    ru: chrono.ru,
    uk: chrono.uk,
  };
  return parsers[lang] || chrono.en;
};

// Four common choices, shown up front with the time they resolve to.
const quickSuggestions = computed(() => [
  {
    key: 'in_1_hour',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.IN_1_HOUR'),
    getDate: () => addHours(new Date(), 1),
  },
  {
    key: 'tomorrow_9am',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.TOMORROW_9AM'),
    getDate: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    },
  },
  {
    key: 'tomorrow_2pm',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.TOMORROW_2PM'),
    getDate: () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(14, 0, 0, 0);
      return tomorrow;
    },
  },
  {
    key: 'next_monday_9am',
    label: t('CONVERSATION.SCHEDULE_MESSAGE.NEXT_MONDAY_9AM'),
    getDate: () => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
      const nextMonday = new Date();
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      nextMonday.setHours(9, 0, 0, 0);
      return nextMonday;
    },
  },
]);

// Short resolved time under each quick choice, e.g. "mié 30, 9:00".
const formatShort = date =>
  format(date, 'EEE d, p', { locale: getDateFnsLocale() });

// Formatted preview of selected date
const formattedPreview = computed(() => {
  if (!parsedDate.value) return '';
  const dateLocale = getDateFnsLocale();
  return format(parsedDate.value, 'PPPP p', { locale: dateLocale });
});

// Check if we can schedule
const canSchedule = computed(() => {
  return (
    parsedDate.value &&
    !errorMessage.value &&
    selectedTimezone.value &&
    !isSubmitting.value
  );
});

// Parse natural language input
const parseNaturalLanguage = text => {
  if (!text || text.trim() === '') {
    return null;
  }

  const trimmedText = text.trim().toLowerCase();

  // Handle "now" explicitly
  const nowKeywords = [
    'now',
    'ahora',
    'agora',
    'maintenant',
    'jetzt',
    'nu',
    '今',
  ];
  if (nowKeywords.includes(trimmedText)) {
    return addMinutes(new Date(), 1); // Schedule at least 1 minute in the future
  }

  // Get the appropriate parser for the current locale
  const parser = getChronoParser();

  // Use chrono to parse natural language with locale support
  const results = parser.parse(trimmedText, new Date(), { forwardDate: true });

  if (results.length > 0) {
    return results[0].start.date();
  }

  return null;
};

// Handle input change
const handleInput = () => {
  selectedKey.value = '';
  errorMessage.value = '';
  const parsed = parseNaturalLanguage(inputValue.value);

  if (parsed) {
    // Check if date is in the past
    const now = startOfMinute(new Date());
    if (isBefore(parsed, now)) {
      errorMessage.value = t(
        'CONVERSATION.SCHEDULE_MESSAGE.PAST_DATE_ERROR',
        'Cannot schedule for a time in the past'
      );
      parsedDate.value = null;
      return;
    }

    parsedDate.value = parsed;
  } else if (inputValue.value.trim() !== '') {
    errorMessage.value = t(
      'CONVERSATION.SCHEDULE_MESSAGE.INVALID_DATE_ERROR',
      'Could not parse date. Try "tomorrow at 2pm" or "in 1 hour"'
    );
    parsedDate.value = null;
  } else {
    parsedDate.value = null;
  }
};

// Handle quick suggestion click
const selectSuggestion = suggestion => {
  const date = suggestion.getDate();

  // Check if date is in the past
  const now = startOfMinute(new Date());
  if (isBefore(date, now)) {
    errorMessage.value = t(
      'CONVERSATION.SCHEDULE_MESSAGE.PAST_DATE_ERROR',
      'Cannot schedule for a time in the past'
    );
    parsedDate.value = null;
    return;
  }

  inputValue.value = '';
  parsedDate.value = date;
  selectedKey.value = suggestion.key;
  errorMessage.value = '';
};

// Handle schedule button click
const handleSchedule = async () => {
  if (!canSchedule.value) return;

  isSubmitting.value = true;

  try {
    emit('schedule', {
      scheduledAt: parsedDate.value.toISOString(),
      timezone: selectedTimezone.value,
    });
  } finally {
    isSubmitting.value = false;
  }
};

// Handle close
const handleClose = () => {
  emit('close');
};

// Handle click outside
const handleClickOutside = () => {
  if (props.show) {
    handleClose();
  }
};

// Reset state when popover opens
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      inputValue.value = '';
      parsedDate.value = null;
      errorMessage.value = '';
      isSubmitting.value = false;
      selectedKey.value = '';
      showSettings.value = false;
    }
  }
);
</script>

<template>
  <OnClickOutside @trigger="handleClickOutside">
    <div
      v-if="show"
      data-testid="schedule-popover"
      class="absolute right-0 z-50 mb-2 overflow-hidden border shadow-lg bottom-full w-[26rem] max-w-[calc(100vw-2rem)] bg-n-solid-2 border-n-container rounded-xl"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-container"
      >
        <div class="flex items-center gap-2">
          <span class="w-4 h-4 i-lucide-clock text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.SCHEDULE_MESSAGE.TITLE') }}
          </span>
        </div>
        <button
          type="button"
          :aria-label="t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
          class="flex items-center justify-center p-0 rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12"
          @click="handleClose"
        >
          <span class="w-4 h-4 i-lucide-x" />
        </button>
      </div>

      <div class="flex flex-col gap-3 p-4">
        <div
          class="grid grid-cols-2 gap-2"
          data-testid="schedule-quick-options"
        >
          <button
            v-for="suggestion in quickSuggestions"
            :key="suggestion.key"
            type="button"
            :data-testid="`schedule-quick-${suggestion.key}`"
            :aria-pressed="selectedKey === suggestion.key"
            class="flex flex-col items-start gap-0.5 m-0 px-3 py-2 text-start border border-solid rounded-lg transition-colors"
            :class="
              selectedKey === suggestion.key
                ? 'border-n-brand bg-n-brand/10 ring-1 ring-n-brand'
                : 'border-n-weak bg-n-solid-1 hover:border-n-strong'
            "
            @click="selectSuggestion(suggestion)"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ suggestion.label }}
            </span>
            <span class="text-xs capitalize text-n-slate-10">
              {{ formatShort(suggestion.getDate()) }}
            </span>
          </button>
        </div>

        <label
          class="flex flex-col gap-1.5 m-0 text-xs font-medium leading-4 text-n-slate-11"
        >
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.OTHER_TIME') }}
          <span class="relative block">
            <input
              v-model="inputValue"
              type="text"
              data-testid="schedule-other-time"
              :placeholder="t('CONVERSATION.SCHEDULE_MESSAGE.PLACEHOLDER')"
              class="block w-full h-9 m-0 text-sm border border-solid rounded-lg outline-none reset-base ps-3 pe-9 bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-1"
              :class="
                errorMessage
                  ? 'border-n-ruby-8 focus:ring-n-ruby-8'
                  : 'border-n-weak focus:border-n-brand focus:ring-n-brand'
              "
              @input="handleInput"
              @keydown.enter.prevent="handleSchedule"
            />
            <span
              v-if="inputValue && (parsedDate || errorMessage)"
              class="absolute w-4 h-4 -translate-y-1/2 end-3 top-1/2"
              :class="
                parsedDate
                  ? 'i-lucide-check text-n-teal-11'
                  : 'i-lucide-alert-circle text-n-ruby-11'
              "
            />
          </span>
        </label>

        <p
          v-if="errorMessage"
          class="m-0 text-xs text-n-ruby-11"
          data-testid="schedule-error"
        >
          {{ errorMessage }}
        </p>
        <p
          v-else-if="formattedPreview"
          class="flex items-center gap-1.5 m-0 text-xs text-n-slate-12"
          data-testid="schedule-summary"
        >
          <span class="w-3.5 h-3.5 i-lucide-calendar-check text-n-teal-11" />
          {{
            t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_FOR', {
              date: `${formattedPreview} (${simplifiedTimezone})`,
            })
          }}
        </p>
      </div>

      <!-- Settings: timezone, out of the way until needed. -->
      <div
        class="px-4 py-2.5 border-t border-n-container"
        data-testid="schedule-settings"
      >
        <div class="flex items-center justify-between gap-2 text-xs">
          <span class="flex items-center min-w-0 gap-1.5 text-n-slate-11">
            <span class="w-3.5 h-3.5 shrink-0 i-lucide-globe" />
            <span class="truncate">
              {{ t('CONVERSATION.SCHEDULE_MESSAGE.TIMEZONE') }}
              <span class="ms-1 text-n-slate-12">{{ timezoneLabel }}</span>
            </span>
          </span>
          <button
            type="button"
            data-testid="schedule-settings-toggle"
            :aria-expanded="showSettings"
            class="m-0 p-0 text-xs font-medium shrink-0 text-n-brand hover:underline"
            @click="showSettings = !showSettings"
          >
            {{ t('CONVERSATION.SCHEDULE_MESSAGE.CHANGE') }}
          </button>
        </div>
        <span v-if="showSettings" class="relative block mt-2">
          <select
            v-model="selectedTimezone"
            data-testid="schedule-timezone"
            :aria-label="t('CONVERSATION.SCHEDULE_MESSAGE.SELECT_TIMEZONE')"
            class="block w-full h-9 m-0 text-sm border border-solid rounded-lg outline-none appearance-none cursor-pointer bg-none reset-base ps-3 pe-9 bg-n-solid-1 border-n-weak text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
          >
            <option
              v-for="tz in timezoneOptions"
              :key="tz.value"
              :value="tz.value"
            >
              {{ timezoneOptionLabel(tz) }}
            </option>
          </select>
          <span
            class="absolute w-4 h-4 -translate-y-1/2 pointer-events-none i-lucide-chevron-down end-3 top-1/2 text-n-slate-10"
          />
        </span>
      </div>

      <div
        class="flex items-center justify-end gap-2 px-4 py-3 border-t border-n-container"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="isSubmitting"
          @click="handleClose"
        >
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          data-testid="schedule-submit"
          :disabled="!canSchedule"
          :is-loading="isSubmitting"
          @click="handleSchedule"
        >
          <template #icon>
            <KaptionSendIcon :size="16" />
          </template>
          {{ t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULE') }}
        </Button>
      </div>
    </div>
  </OnClickOutside>
</template>
